import Header from 'components/Header/Header';
import MobileHeader from 'components/Header/MobileHeader';
import ClassicMobileNav from 'components/Nav/classic/ClassicMobileNav';
import ClassicSidebar from 'components/Nav/classic/ClassicSidebar';
import MobileNav from 'components/Nav/MobileNav';
import Sidebar from 'components/Nav/Sidebar';
import { usePerms } from 'contexts/PermsContext';
import GetWidgetData from 'data/widgetData/widgetData';
import { MessagingProvider, ResponderMessenger } from 'features/Responder/features/Messenger';
import { isResponderMessengerEnabled } from 'features/Responder/features/Messenger/config/voipFeatureFlag';
import { useCurrentUserId } from 'features/Responder/features/Messenger/hooks/useCurrentUserId';
import useWindow from 'hooks/useWindow';
import { useSelector } from 'react-redux';
import { Outlet, useLocation } from 'react-router-dom';
import AlarmListener from 'services/Alarms/AlarmListener';
import { NAVIGATION_STYLES } from 'store/config/displayOptions.config';
import { selectNavigationStyle } from 'store/selectors/DisplayOptions/displayOptionsSelectors';

const AppLayout = ({
  setSidebarOpen,
  routeName,
  accessLevel,
  updateSidebar,
  widgetRefresh,
  setWidgetRefresh,
  sidebarOpen,
  setMobileNavOpen,
  mobileNavOpen,
  setShowFull,
  setHasFetchedPerms,
  setCookiesAgreed,
  setPrivacyAgreed,
  setNewTermsFullyAgreed,
  setTermsFullyViewed,
  setHasSignedIn,
}) => {
  const location = useLocation();
  const { width } = useWindow();
  const perms = usePerms();
  // The display settings choose the Merlin Slate navigation or the Classic one.
  const isClassicNavigation = useSelector(selectNavigationStyle) === NAVIGATION_STYLES.CLASSIC;
  const DesktopNavigation = isClassicNavigation ? ClassicSidebar : Sidebar;
  const MobileNavigation = isClassicNavigation ? ClassicMobileNav : MobileNav;
  const isResponderRoute = location.pathname.includes('/responder');
  const isIntegrationsRoute = location.pathname.includes('/system/integrations');
  const usesNativePageScrolling = location.pathname === '/dashboard';
  const { currentUserId } = useCurrentUserId();
  const messengerEnabled = isResponderMessengerEnabled();

  const layout = (
    <div
      className="d-flex w-screen h-screen bg-background"
      style={{
        backgroundSize: 'cover',
        backgroundRepeat: 'repeat',
        flex: 1,
      }}
    >
      {perms.testPermission([8, 9, 10, 11, 12, 13], ['PendingAlarms']) && <AlarmListener />}
      {width > 992 ? (
        <DesktopNavigation
          onOpenChanged={setSidebarOpen}
          routeName={routeName}
          accessLevel={accessLevel}
          updateSidebar={updateSidebar}
          definedWidgetData={GetWidgetData(widgetRefresh, setWidgetRefresh, sidebarOpen)}
        />
      ) : (
        <MobileNavigation isOpen={setMobileNavOpen} open={mobileNavOpen} />
      )}
      <div
        id={usesNativePageScrolling ? undefined : 'main-scroll-container'}
        className="customScrollbar-Y"
        style={{
          width: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {width > 992 ? (
          <Header
            setHasSignedIn={setHasSignedIn}
            routeName={routeName}
            widgetRefresh={widgetRefresh}
            setWidgetRefresh={setWidgetRefresh}
            setShowFull={setShowFull}
            setNewTermsFullyAgreed={setNewTermsFullyAgreed}
            setTermsFullyViewed={setTermsFullyViewed}
            setHasFetchedPerms={setHasFetchedPerms}
            setCookiesAgreed={setCookiesAgreed}
            setPrivacyAgreed={setPrivacyAgreed}
          />
        ) : (
          <MobileHeader
            isMobileNavOpen={setMobileNavOpen}
            setHasSignedIn={setHasSignedIn}
            setShowFull={setShowFull}
            setNewTermsFullyAgreed={setNewTermsFullyAgreed}
            setTermsFullyViewed={setTermsFullyViewed}
            setCookiesAgreed={setCookiesAgreed}
            setPrivacyAgreed={setPrivacyAgreed}
            setHasFetchedPerms={setHasFetchedPerms}
          />
        )}
        <div
          className={`main-content-area p-0 p-md-3${
            isIntegrationsRoute ? ' integrations-main-content-area' : ''
          }`}
        >
          <Outlet />
        </div>
      </div>
      {messengerEnabled && isResponderRoute && <ResponderMessenger />}
    </div>
  );

  if (!messengerEnabled) return layout;

  return <MessagingProvider currentUserId={currentUserId}>{layout}</MessagingProvider>;
};

export default AppLayout;
